import React, { useEffect, useRef } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useMutation } from '@apollo/client';
import { Formik, FormikProps } from 'formik';
import router from 'next/router';

import { withAuth } from '@/components/with_auth';
import Page from '@/components/layout/page';
import { showAlertOnboarding, onboardingAtom, userAtom } from '@/components/with_auth/State';
import routes from '@/services/routes';
import { UPDATE_USER } from '@/components/onboarding/Actions';
import Button, { ButtonSize, ButtonType } from '@/components/commons/button';
import Title from '@/components/commons/page-elements/Title';
import Plus from '@/components/commons/icons/Plus';
import ThumbsUpOutline from '@/components/commons/icons/ThumbsUpOutline';
import { onErrorHandler, onCompletedHandler, handleValidationErrors } from '@/services/apollo';
import { reportGeneralError, haveErrors } from '@/components/onboarding/Shared';

import AlertsTable from '@/components/calendar/alerts/AlertsTable';

function Alerts() {
    const [user, setRecoilUser] = useRecoilState(userAtom);
    const isAlertOnboarding = useRecoilValue(showAlertOnboarding);
    const [onboardingData, setOnboardingData] = useRecoilState(onboardingAtom);

    const [updateUser] = useMutation(UPDATE_USER);
    const alertStatusformRef = useRef<FormikProps<any>>(null);

    const handleAlertStatus = () => {
        if (alertStatusformRef.current) {
            alertStatusformRef.current.handleSubmit();
        }
    };

    useEffect(() => {
        if (onboardingData.type !== 'alert') {
            setOnboardingData({
                ...onboardingData,
                type: 'alert',
            });
        }
    });

    const handleFormSubmit = (_values, { setSubmitting, setStatus }) => {
        updateUser({
            variables: {
                updateUserInput: {
                    attributes: {
                        hasCreatedAlerts: true,

                    },
                },
            },
            notifyOnNetworkStatusChange: true,
            fetchPolicy: 'no-cache',
            onError: onErrorHandler((error) => {
                setSubmitting(false);
                reportGeneralError(error);
            }),
            onCompleted: onCompletedHandler(() => { }, (data) => {
                setSubmitting(false);
                const { errors } = data.updateUser;
                if (haveErrors(errors)) {
                    handleValidationErrors(errors, setStatus, 'user');
                    return;
                }

                setRecoilUser(data.updateUser.user);
                setStatus({ errors: {} });

                router.push(routes.MY_SIXTY);
            }),
        });
    };

    return (
        <Page>
            <div className="flex flex-wrap fullHeightLessNavPage bg-lightGray bg-opacity-30 items-start">
                <div className="w-full p-4 pt-8 sm:p-8">
                    <div className="flex justify-between relative flex-col md:flex-row">
                        <div className="text-left mb-4 w-full">
                            <Title
                                title="Smart Alerts"
                                externalStyles="mb-8"
                            >
                                <div className="flex flex-wrap gap-2 justify-between items-center w-full">
                                    <p>
                                        With custom alerts, Sixty can help ensure you never
                                        miss important events.
                                    </p>
                                    <div className="flex flex-wrap gap-2 mt-4 sm:mt-0 w-full sm:w-auto">
                                        {isAlertOnboarding && (
                                            <Formik
                                                innerRef={alertStatusformRef}
                                                onSubmit={handleFormSubmit}
                                                initialValues={{
                                                    hasCreatedAlerts: user?.hasCreatedAlerts,
                                                }}
                                            >
                                                {() => (
                                                    <Button
                                                        size={ButtonSize.base}
                                                        icon={<ThumbsUpOutline className="h-4 w-4 mr-3" />}
                                                        text="I'm done"
                                                        type={ButtonType.submit}
                                                        onClick={handleAlertStatus}
                                                    />
                                                )}
                                            </Formik>
                                        )}
                                        <Button
                                            size={ButtonSize.base}
                                            text="Add alert"
                                            type={ButtonType.submit}
                                            onClick={() => router.push(routes.ADD_ALERT)}
                                            icon={<Plus className="mr-2" />}
                                        />
                                    </div>
                                </div>
                            </Title>
                        </div>
                    </div>
                    <div className="drop-fix">
                        <AlertsTable />
                    </div>
                </div>
            </div>

        </Page>
    );
}

export default withAuth(Alerts);
